import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, userService, attendanceService } from '../services';

const InstructorDashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ trades: 0, attendanceTaken: 0, present: 0 });
  const [context, setContext] = useState(null);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      const user = auth.getUser();
      setCurrentUser(user);
      loadDashboardCounts(user);
    }
  }, []);

  const loadDashboardCounts = async (user) => {
    try {
      const userId = user?._id || user?.id;
      const [instructorContext, instructorAttendance] = await Promise.all([
        userService.getInstructorContext(userId).catch(() => null),
        attendanceService.getInstructorAttendance(userId).catch(() => []),
      ]);
      setContext(instructorContext);
      const attendanceList = Array.isArray(instructorAttendance) ? instructorAttendance : [];

      setCounts({
        trades: Array.isArray(instructorContext?.instructor?.assignedTrades)
          ? instructorContext.instructor.assignedTrades.length
          : 0,
        attendanceTaken: attendanceList.length,
        present: attendanceList.filter((r) => r.status === 'present').length,
      });
    } catch (error) {
      console.error('Error loading instructor dashboard counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const name = currentUser?.fullName || currentUser?.username || 'Instructor';

  const statsCards = [
    { icon: '📚', label: 'Assigned Trades', count: counts.trades, accent: '#F59E0B', bg: 'rgba(245,158,11,0.12)', nav: null },
    { icon: '✅', label: 'Attendance Records', count: counts.attendanceTaken, accent: '#8B5CF6', bg: 'rgba(139,92,246,0.12)', nav: '/instructor/attendance' },
    { icon: '📊', label: 'Present (All Time)', count: counts.present, accent: '#34D399', bg: 'rgba(52,211,153,0.12)', nav: '/instructor/reports' },
  ];

  return (
    <div className="db-page">
      {/* ─── Shared Scoped CSS for Admin/Instructor Dashboards ─── */}
      <style>{`
        /* Replicating the exact same styling tokens from Dashboard.js */
        .db-page {
          padding: 28px 28px 40px;
          display: flex;
          flex-direction: column;
          gap: 28px;
          min-height: 100%;
        }

        .db-hero {
          border-radius: 12px;
          padding: 36px 40px;
          background: var(--primary-blue);
          border: 1px solid rgba(59,130,246,0.18);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          position: relative;
          overflow: hidden;
        }
        .db-hero::before {
          content: '';
          position: absolute; top: -80px; right: -80px;
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .db-hero::after {
          content: '';
          position: absolute; bottom: -60px; left: 40%;
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .db-hero-text { flex: 1; position: relative; z-index: 1; }
        .db-hero-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(59,130,246,0.1);
          border: 1px solid rgba(59,130,246,0.25);
          border-radius: 12px;
          padding: 4px 12px;
          font-size: 11px; font-weight: 600;
          color: #60A5FA;
          letter-spacing: 0.5px; text-transform: uppercase;
          margin-bottom: 14px;
        }
        .db-hero-title {
          font-family: inherit;
          font-size: 30px; font-weight: 800;
          line-height: 1.2; letter-spacing: -0.5px;
          margin-bottom: 10px;
          color: #E2E8F0;
        }
        .db-hero-title span {
          background: var(--primary-blue);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .db-hero-sub {
          font-size: 14px; color: var(--muted);
          line-height: 1.6; max-width: 380px; margin-bottom: 24px;
        }
        .db-hero-actions { display: flex; gap: 10px; flex-wrap: wrap; }
        .db-btn-primary {
          padding: 11px 22px;
          background: var(--primary-blue);
          color: #fff;
          border: none; border-radius: 8px;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit;
          transition: all 0.2s; letter-spacing: 0.2px;
        }
        .db-btn-primary:hover { opacity: 0.92; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .db-btn-ghost {
          padding: 11px 22px;
          background: var(--border-light);
          color: #E2E8F0;
          border: 1px solid var(--border-light);
          border-radius: 8px;
          font-size: 13px; font-weight: 600; cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }
        .db-btn-ghost:hover { background: var(--border-light); border-color: var(--border-light); }

        .db-hero-visual {
          position: relative; z-index: 1;
          width: 140px; height: 140px; flex-shrink: 0;
        }
        .db-hero-ring {
          position: absolute; inset: 0;
          border-radius: 50%;
          border: 2px solid rgba(59,130,246,0.3);
          animation: dbSpin 12s linear infinite;
        }
        .db-hero-ring::before {
          content: '';
          position: absolute; top: -4px; left: 50%;
          transform: translateX(-50%);
          width: 8px; height: 8px;
          background: #3B82F6; border-radius: 50%;
        }
        @keyframes dbSpin { to { transform: rotate(360deg); } }
        .db-hero-ring2 {
          position: absolute; inset: 16px;
          border-radius: 50%;
          border: 1px dashed rgba(139,92,246,0.3);
          animation: dbSpin 20s linear infinite reverse;
        }
        .db-hero-emoji {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 64px;
        }

        .db-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .db-stat-card {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 12px;
          padding: 24px;
          transition: all 0.25s;
          position: relative;
          overflow: hidden;
        }
        .db-stat-card.clickable {
          cursor: pointer;
        }
        .db-stat-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: var(--c);
          opacity: 0.85;
        }
        .db-stat-card:hover {
          transform: translateY(-3px);
          border-color: var(--border-light);
          box-shadow: var(--shadow-md);
        }
        .db-stat-card:not(.clickable):hover {
          transform: none;
          box-shadow: none;
        }
        .db-stat-icon-wrap {
          width: 48px; height: 48px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; flex-shrink: 0; margin-bottom: 16px;
          background: var(--bg-i);
        }
        .db-stat-content { display: flex; flex-direction: column; }
        .db-stat-num {
          font-family: inherit;
          font-size: 32px; font-weight: 800; line-height: 1;
          color: var(--text-dark); margin-bottom: 4px;
        }
        .db-stat-lbl {
          font-size: 13px; color: var(--text-light);
          font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
        }

        @media (max-width: 1024px) {
          .db-hero { padding: 32px; flex-direction: column; text-align: center; }
          .db-hero-actions { justify-content: center; }
          .db-hero::before { right: -150px; }
          .db-hero-sub { margin: 0 auto 24px; }
        }
        @media (max-width: 768px) {
          .db-page { padding: 16px 16px 30px; gap: 20px; }
          .db-hero-title { font-size: 26px; }
          .db-stats-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 480px) {
          .db-stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Hero Section */}
      <div className="db-hero">
        <div className="db-hero-text">
          <div className="db-hero-eyebrow">✦ Instructor Portal</div>
          <h1 className="db-hero-title">
            Welcome back, <br />
            <span>{name}</span>
          </h1>
          <p className="db-hero-sub">
            Take attendance for your assigned trades in the current running secondary cohort.
          </p>
          {Array.isArray(context?.instructor?.assignedTrades) && context.instructor.assignedTrades.length > 0 && (
            <p className="db-hero-sub" style={{ marginTop: -8 }}>
              Trades: <strong>{context.instructor.assignedTrades.join(', ')}</strong>
              {context?.batch?.name ? ` · Cohort: ${context.batch.name}` : ' · No open cohort'}
            </p>
          )}
          <div className="db-hero-actions">
            <button className="db-btn-primary" onClick={() => navigate('/instructor/attendance')}>
              Take Attendance
            </button>
            <button className="db-btn-ghost" onClick={() => navigate('/instructor/reports')}>
              Attendance Report
            </button>
          </div>
        </div>

        <div className="db-hero-visual">
          <div className="db-hero-ring" />
          <div className="db-hero-ring2" />
          <div className="db-hero-emoji">🎓</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="db-stats-grid">
        {statsCards.map((st, i) => (
          <div
            key={i}
            className={`db-stat-card${st.nav ? ' clickable' : ''}`}
            style={{ '--c': st.accent, '--bg-i': st.bg }}
            onClick={() => { if (st.nav) navigate(st.nav); }}
          >
            <div className="db-stat-icon-wrap">{st.icon}</div>
            <div className="db-stat-content">
              <div className="db-stat-num">{loading ? '—' : st.count}</div>
              <div className="db-stat-lbl">{st.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InstructorDashboard;
